import {
  isPasswordValidator,
  isNotEmptyTrimmedStringValidator,
  isEmailValidator,
  isValueValidator,
} from '@currency-one/validators'
import cookieService from 'services/cookie-service'
import { pushDataLayer } from 'utils/data-layer-push'
import { t9n } from 't9n/t9n'
import { getCurrentLanguage } from '../utils/current-language'
import { RegisterPayload } from 'models/endpoints-payload'
import { checkEmailAvailability } from 'services/check-email-availability'
import { getCsrfToken } from 'services/csrf-token'
import { registerUser } from 'services/register-user'
import { collectDataToSend, getElementsForm } from './form-handling-competition'

type FormContainer = Document | HTMLElement
type StringValidator = (value: string, selector: string, formContainer: FormContainer) => string
type BoleanValidator = (value: boolean, selector: string, formContainer: FormContainer) => boolean

declare const USER_BASE_URL_WT: string
const currentLanguage = getCurrentLanguage()

// Function reset all error spans for form and remove is-invalid class from inputs.
export const resetAllErrorMSG = (formContainer: FormContainer): void => {
  const $errorMsgsSpans = formContainer.querySelectorAll<HTMLSpanElement>('[data-error-field]')
  const $invalidInputs = formContainer.querySelectorAll<HTMLInputElement>('.is-invalid')
  const $serverError = formContainer.querySelector<HTMLParagraphElement>('[data-error-server]')

  $errorMsgsSpans.forEach(($singleErrorSpan) => {
    $singleErrorSpan.hidden = true
  })

  $invalidInputs.forEach(($singleInvalidField) => {
    $singleInvalidField.classList.remove('is-invalid')
  })

  if ($serverError) $serverError.hidden = true
}

// Function set error MSG for span and highlight invalid form field.
export const setInputErrorMSG = (selector: string, errorMSG: string, formContainer: FormContainer): void => {
  const $inputWithError = formContainer.querySelector<HTMLElement>(selector)

  let $inputParentWithError = $inputWithError
  let $errorSpanElement = null

  // Lets traverse DOM and search for parent that contain <span> marked as label for error MSG
  do {
    $inputParentWithError = $inputParentWithError.parentNode as HTMLElement
    $errorSpanElement = $inputParentWithError.querySelector<HTMLSpanElement>('[data-error-field]')
  } while ($errorSpanElement === null)

  if ($errorSpanElement) {
    $errorSpanElement.innerHTML = errorMSG
    $errorSpanElement.hidden = false
  }

  if ($inputWithError) {
    $inputWithError.classList.add('is-invalid')
  }
}

// Function set error MSG for span and highlight invalid form field.
export const setErrorMSG = (selector: string, errorMSG: string, formContainer: FormContainer): void => {
  const $errorLabel = formContainer.querySelector<HTMLElement>(selector)

  if ($errorLabel) {
    $errorLabel.innerHTML = errorMSG
    $errorLabel.hidden = false
  }
}

/**
 * Function checks if array of test is empty. If not set error label based on detected error
 * and add is-invalid class for input.
 */
export const checkValidity = (checkResults: Array<string>, selector: string, formContainer: FormContainer): boolean => {
  checkResults = checkResults.filter((n) => n)
  if (checkResults.length) {
    setInputErrorMSG(selector, checkResults[0], formContainer)
    return false
  }
  return true
}

// Validator for email input field.
export const emailValidator = (value: string, selector: string, formContainer: FormContainer): string | null => {
  const isValid = [
    !isNotEmptyTrimmedStringValidator(value) ? t9n('formValidation.emptyField', currentLanguage) : '',
    !isEmailValidator(value) ? t9n('formValidation.emailNotValid', currentLanguage) : '',
  ]
  return checkValidity(isValid, selector, formContainer) ? value : null
}

// Validator for password input field
export const passwordValidator = (value: string, selector: string, formContainer: FormContainer): string | null => {
  const isValid = [
    !isNotEmptyTrimmedStringValidator(value) ? t9n('formValidation.emptyField', currentLanguage) : '',
    !isPasswordValidator(value) ? t9n('formValidation.passwordToShort', currentLanguage) : '',
  ]
  return checkValidity(isValid, selector, formContainer) ? value : null
}

// Validator for checkbox input field.
export const consentValidator = (value: boolean, selector: string, formContainer: FormContainer): boolean | null => {
  const isValid = [!isValueValidator(value) ? t9n('formValidation.requiredField', currentLanguage) : '']
  return checkValidity(isValid, selector, formContainer) ? value : null
}

// Function retrieves and validate the value of an input field.
export const getStringField = (
  selector: string,
  validator: StringValidator,
  formContainer: FormContainer,
): string | null => {
  const ALLOWED_INPUT_TYPES = ['text', 'hidden', 'password', 'textarea']
  const FALLBACK_RETURN_VALUE = null
  const $inputElement = formContainer.querySelector<HTMLInputElement | HTMLTextAreaElement>(selector)
  if ($inputElement) {
    return ALLOWED_INPUT_TYPES.includes($inputElement.type)
      ? validator(String($inputElement.value).trim(), selector, formContainer)
      : FALLBACK_RETURN_VALUE
  }
  return FALLBACK_RETURN_VALUE
}

// Function retrieves and validate the value of an checkbox field.
export const getBooleanField = (
  selector: string,
  validator: BoleanValidator,
  formContainer: FormContainer,
): boolean | null => {
  const ALLOWED_INPUT_TYPES = ['checkbox']
  const FALLBACK_RETURN_VALUE = null
  const $inputElement = formContainer.querySelector<HTMLInputElement>(selector)
  if ($inputElement) {
    return ALLOWED_INPUT_TYPES.includes($inputElement.type)
      ? validator($inputElement.checked, selector, formContainer)
      : FALLBACK_RETURN_VALUE
  }
  return FALLBACK_RETURN_VALUE
}

// Function fetches data from form and create payload object.
export const getFormData = (formContainer: FormContainer): RegisterPayload => {
  const promocode = formContainer.querySelector<HTMLInputElement>('[data-promocode-field]')?.value
  return {
    email: getStringField('[data-email-field]', emailValidator, formContainer),
    password: getStringField('[data-password-field]', passwordValidator, formContainer),
    promocode: promocode ? promocode : '',
    special_offer_terms_accepted: getBooleanField('[data-checkbox-field="promotion"]', consentValidator, formContainer),
    terms_and_conditions_accepted: getBooleanField('[data-checkbox-field="terms"]', consentValidator, formContainer),
    region: 'PL', // deprecated stuff - long history of WT
    language: currentLanguage,
  }
}

// Check if payload do not contain any null value that indicates form is invalid.
export const checkIfPayloadComplete = (input: object): boolean => {
  return !Object.values(input).some((value) => value === null)
}

const handleFormSubmit = (formContainer: FormContainer = document, isWithCompetition: boolean = false): void => {
  resetAllErrorMSG(formContainer)
  const payload = getFormData(formContainer)
  if (checkIfPayloadComplete(payload)) {
    // Response was resolved as HTTP 200 (ok)
    checkEmailAvailability(payload)
      .then(() => {
        // Grab CSRF Token to payload
        getCsrfToken()
          .then((token) => {
            registerUser({ ...payload, _csrf: token })
              .then((/* res */) => {
                cookieService.setItem('confirm_email', payload.email, 0.05) // TTL: 1H 20M 0S

                pushDataLayer('form_submitted', { form_name: 'Rejstracja z landingu', form_url: window.location.href })
              })
              .then(() => {
                if (isWithCompetition) {
                  const competitionFrom = getElementsForm()
                  collectDataToSend(competitionFrom)
                }
                window.location.href = `${USER_BASE_URL_WT}/?lang=${payload.language}#/confirm-email`
              })
              .catch((err) => {
                console.error(`User register failed: ${err}`)
              })
          })
          .catch((err) => {
            console.error(`CSRF token invalid: ${err}`)
          })
        // Send Request
      })
      .catch((/* res */) => {
        // TODO: Should consider HTTP status code check, to distinguish API response vs API fail
        setInputErrorMSG('[data-email-field]', t9n('formValidation.emailReserved', currentLanguage), formContainer)
      })
  }
}

export const handleShowPassword = ($showPasswordButton: HTMLButtonElement, $passwordInput: HTMLInputElement): void => {
  if ($passwordInput) {
    $passwordInput.type = `${$passwordInput.type === 'text' ? 'password' : 'text'}`
    $showPasswordButton.textContent = `${
      $passwordInput.type === 'text' ? t9n('password.hide', currentLanguage) : t9n('password.show', currentLanguage)
    }`
  }
}

export default function initForm(isWithCompetition: boolean = false): void {
  const $submitButton = document.querySelector<HTMLButtonElement>('[data-submit-form]')
  const $showPasswordButton = document.querySelector<HTMLButtonElement>('[data-show-password-button]')
  const $passwordInput = document.querySelector<HTMLInputElement>('[data-password-field]')
  if ($submitButton) $submitButton.addEventListener('click', () => handleFormSubmit(document, isWithCompetition))
  if ($showPasswordButton)
    $showPasswordButton.addEventListener('click', () => handleShowPassword($showPasswordButton, $passwordInput))
}

export function initExitPopupForm(): void {
  const $form = document.querySelector<HTMLButtonElement>('[data-exit-popup]')
  const $submitButton = $form?.querySelector<HTMLButtonElement>('[data-submit-form]')
  const $showPasswordButton = $form?.querySelector<HTMLButtonElement>('[data-show-password-button]')
  const $passwordInput = $form?.querySelector<HTMLInputElement>('[data-password-field]')
  if ($submitButton) $submitButton.addEventListener('click', () => handleFormSubmit($form))
  if ($showPasswordButton)
    $showPasswordButton.addEventListener('click', () => handleShowPassword($showPasswordButton, $passwordInput))
}
